import { useState, useEffect } from 'react'
import styled from 'styled-components'
import { supabase } from '../../supabase'
import { ecrire, lire } from './erreurs'
import { VARIABLES, VARIABLES_LIVRAISON, CAS_LIVRAISON, MODELES_SUGGERES, MODELE_DEVIS, remplir } from './mailModeles'
import {
  t, Stagger, Panel, PanelHead, PanelTitle, PageHead, PageTitle, PageSub,
  Btn, Input, Textarea, Label, Field, Cluster, Pill,
  ListItem, ItemTitle, ItemMeta, Loading, Muted, Chip, useUI,
} from './ui'

const VIDE = { nom: '', sujet: '', corps: '', phrases: {}, actif: true }

/* Devis fictif : l'aperçu doit montrer un vrai texte, pas des
   accolades. Les valeurs sont volontairement reconnaissables.

   Deux jeux plutôt qu'un : `{{livraison}}` change du tout au tout
   selon la marchandise, et un aperçu de liste d'attente affichant
   « votre date est réalisable » se contredisait lui-même. */
const EXEMPLE = {
  nom: 'Marc Dupont',
  produits: 'Paille défibrée',
  quantite: '24 T',
  detail: '--- Paille défibrée ---\n- Poids total approximatif : 24 T\n'
    + '- Prix unitaire : 28.00 CHF/100kg\n- Sous-total : 6 720.00 CHF',
  total: '6 720.00 CHF',
  livraison: 'Délai de livraison : la date que vous souhaitez, le 28 août 2026, '
    + 'est réalisable. Comptez 3 à 7 jours après validation ; nous confirmons '
    + 'ensemble le jour et le créneau.',
  date_souhaitee: '28 août 2026',
  validite: '31 août 2026',
  recap: 'Concernant votre demande de devis du 16 août 2026 :\n'
    + '- Produits : Paille défibrée\n- Quantité : 24 T\n- Canton : Fribourg',
  date_au_plus_tot: '24 août 2026',
  attente_fourrage: 'la mi/fin-septembre',
  lien_suivi: 'https://achatpaille.com/suivi-devis?q=…',
  pied: 'Pour toute question :\ncontact@fptransports.fr\n'
    + '+33 7 82 20 79 04 (téléphone et WhatsApp)\n\nBien cordialement,\nFrancky – achatpaille.com',
}

/* Variante fourrage : sécheresse et liste d'attente. */
const EXEMPLE_FOURRAGE = {
  ...EXEMPLE,
  produits: 'Foin de Prairie naturelle',
  detail: '--- Foin de Prairie naturelle ---\n- Poids total approximatif : 24 T\n'
    + '- Prix unitaire : 34.00 CHF/100kg\n- Taxe Fourrage : 3.20 CHF/100kg\n'
    + '- Sous-total : 8 160.00 CHF',
  total: '8 160.00 CHF',
  livraison: 'Délai de livraison : en raison de la sécheresse 2026 sur les fourrages '
    + 'et d\'une forte demande, les livraisons de Foin et de Luzerne sont retardées. '
    + 'Votre souhait du 28 août 2026 ne pourra donc pas être tenu. La validation de ce '
    + 'devis vous inscrit sur liste d\'attente, avec une livraison à partir de la '
    + 'mi/fin-septembre.',
}

const SCENARIOS = [
  { cle: 'tenable',  label: 'Paille, date tenable',      valeurs: EXEMPLE },
  { cle: 'fourrage', label: 'Fourrage, liste d\'attente', valeurs: EXEMPLE_FOURRAGE },
]

const Split = styled.div`
  display: grid;
  grid-template-columns: ${(p) => (p.$open ? 'minmax(0, 300px) minmax(0, 1fr)' : '1fr')};
  gap: 20px; align-items: start;
  @media (max-width: 1060px) { grid-template-columns: 1fr; }
`
const Col = styled.div`display: flex; flex-direction: column; gap: 16px;`

const Jetons = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 14px;
`
/* Cliquable : on insère la variable plutôt que de la faire recopier
   à la main, où une faute de frappe passerait inaperçue. */
const Jeton = styled.button`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.7rem; padding: 4px 8px; cursor: pointer;
  border-radius: ${t.rXs}; border: 1px solid ${t.line};
  background: ${t.surfaceSunk}; color: ${t.textSoft};
  transition: all 0.14s;
  &:hover { border-color: ${t.gold}; color: ${t.gold}; background: ${t.goldWash}; }
`
const Details = styled.details`
  border: 1px solid ${t.line}; border-radius: ${t.rSm};
  padding: 11px 14px; margin-bottom: 14px; background: ${t.surfaceAlt};
  summary {
    cursor: pointer; font-family: ${t.font}; font-size: 0.78rem;
    font-weight: 560; color: ${t.textSoft};
  }
  &[open] summary { color: ${t.text}; }
`
const Apercu = styled.pre`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.74rem; line-height: 1.6; color: ${t.textSoft};
  white-space: pre-wrap; word-break: break-word;
  background: ${t.surfaceSunk}; border: 1px solid ${t.line};
  border-radius: ${t.rSm}; padding: 13px; margin: 0;
  max-height: 340px; overflow: auto;
`
const Bascule = styled.button`
  display: flex; align-items: center; gap: 8px;
  padding: 8px 13px; border-radius: ${t.rSm}; cursor: pointer;
  font-family: ${t.font}; font-size: 0.8rem; font-weight: 560;
  background: ${(p) => (p.$on ? t.greenWash : t.surfaceSunk)};
  border: 1px solid ${(p) => (p.$on ? '#CFE6D8' : t.line)};
  color: ${(p) => (p.$on ? t.green : t.textSoft)};
  transition: all 0.14s;
`
const Fige = styled.div`
  border: 1px dashed ${t.line}; border-radius: ${t.rSm};
  padding: 13px 15px; background: ${t.surfaceAlt};
`

export default function ModelesMailAdmin() {
  const { toast, confirm } = useUI()
  const [liste, setListe] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null) // objet | 'new' | null
  const [form, setForm] = useState(VIDE)
  const [saving, setSaving] = useState(false)
  const [scenario, setScenario] = useState('tenable')

  useEffect(() => { charger() }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  const charger = async () => {
    setLoading(true)
    const data = await lire(
      supabase.from('modeles_mail').select('*').order('ordre').order('nom'),
      toast, 'chargement des modèles',
    )
    /* En cas d'échec on garde la liste précédente : la remplacer par
       un tableau vide ferait croire que les modèles ont été perdus. */
    if (data) setListe(data)
    setLoading(false)
  }

  const ouvrir = (m) => {
    setSelected(m)
    setForm({
      nom: m.nom || '', sujet: m.sujet || '', corps: m.corps || '',
      phrases: m.phrases || {}, actif: m.actif !== false,
    })
  }

  const nouveau = (base) => {
    setSelected('new')
    setForm(base ? { ...VIDE, ...base } : VIDE)
  }

  const enregistrer = async () => {
    if (!form.nom.trim()) return toast('Donnez un nom au modèle', 'error')
    setSaving(true)
    const payload = {
      nom: form.nom.trim(), sujet: form.sujet, corps: form.corps,
      /* Les phrases vides ne sont pas enregistrées : une clé absente
         retombe sur la formulation par défaut, une chaîne vide aurait
         supprimé la phrase du mail. */
      phrases: Object.fromEntries(
        Object.entries(form.phrases || {}).filter(([, v]) => v && v.trim()),
      ),
      actif: form.actif, updated_at: new Date().toISOString(),
    }
    const req = selected === 'new'
      ? supabase.from('modeles_mail').insert([{ ...payload, ordre: liste.length }])
      : supabase.from('modeles_mail').update(payload).eq('id', selected.id)
    const ok = await ecrire(req, toast, 'enregistrement du modèle')
    setSaving(false)
    if (!ok) return
    toast(selected === 'new' ? 'Modèle créé' : 'Modèle mis à jour')
    setSelected(null)
    charger()
  }

  const supprimer = async () => {
    const ok = await confirm({
      icon: '🗑️', tone: 'red',
      title: 'Supprimer ce modèle ?',
      body: <><strong>{selected.nom}</strong> ne sera plus proposé à l'envoi. Les mails
        déjà partis ne sont pas concernés.</>,
      confirmLabel: 'Supprimer',
    })
    if (!ok) return
    await supabase.from('modeles_mail').delete().eq('id', selected.id)
    toast('Modèle supprimé')
    setSelected(null)
    charger()
  }

  /* Aperçu du scénario choisi, phrase de délai personnalisée comprise :
     l'écrire sans la voir rendue reviendrait à travailler à l'aveugle. */
  const apercu = () => {
    const sc = SCENARIOS.find((x) => x.cle === scenario)
    const perso = form.phrases?.[sc.cle]?.trim()
    const valeurs = perso
      ? { ...sc.valeurs, livraison: remplir(perso, sc.valeurs) }
      : sc.valeurs
    return remplir(form.corps, valeurs)
  }

  /* Insère la variable là où se trouve le curseur, dans le champ
     que l'on vient de quitter. */
  const inserer = (cle) => {
    const zone = document.getElementById('corps-modele')
    const jeton = `{{${cle}}}`
    if (!zone) return setForm((p) => ({ ...p, corps: `${p.corps}${jeton}` }))
    const { selectionStart: d, selectionEnd: f, value } = zone
    const texte = value.slice(0, d) + jeton + value.slice(f)
    setForm((p) => ({ ...p, corps: texte }))
    requestAnimationFrame(() => {
      zone.focus()
      zone.setSelectionRange(d + jeton.length, d + jeton.length)
    })
  }

  return (
    <>
      <PageHead>
        <div>
          <PageTitle>Modèles de mail</PageTitle>
          <PageSub>
            {liste.length} modèle(s) personnalisé(s) · proposés au moment d'envoyer un devis
          </PageSub>
        </div>
        <Btn $variant="gold" onClick={() => nouveau()}>+ Nouveau modèle</Btn>
      </PageHead>

      <Split $open={!!selected}>
        <Col>
          <Fige>
            <Cluster $gap="8px">
              <ItemTitle>{MODELE_DEVIS.nom}</ItemTitle>
              <Pill $tone="neutral">intégré</Pill>
            </Cluster>
            <ItemMeta>{MODELE_DEVIS.description}</ItemMeta>
            <Muted style={{ fontSize: '0.72rem', marginTop: 7, display: 'block' }}>
              Toujours disponible et non modifiable : c'est le mail de référence.
            </Muted>
          </Fige>

          {loading ? (
            <Loading rows={2} h="70px" />
          ) : (
            liste.map((m, i) => (
              <Stagger key={m.id} $i={i}>
                <ListItem $on={selected?.id === m.id} onClick={() => ouvrir(m)}>
                  <Cluster $gap="8px">
                    <ItemTitle>{m.nom}</ItemTitle>
                    {m.actif === false && <Pill $tone="neutral">masqué</Pill>}
                  </Cluster>
                  <ItemMeta>{m.sujet || 'sans objet'}</ItemMeta>
                </ListItem>
              </Stagger>
            ))
          )}

          {/* Toujours accessible : ce bloc n'était affiché que sur une liste
              vide, si bien que créer un modèle faisait disparaître les
              autres exemples — on croyait les avoir perdus. */}
          {!loading && (
            <Panel>
              <PanelTitle>Partir d'un exemple</PanelTitle>
              <Muted style={{ fontSize: '0.78rem', margin: '6px 0 12px', display: 'block' }}>
                Des modèles prêts à l'emploi, à reprendre et à retoucher. Ils ouvrent
                simplement l'éditeur pré-rempli, rien n'est enregistré avant que vous
                ne le validiez.
              </Muted>
              <Cluster $gap="8px" style={{ flexWrap: 'wrap' }}>
                {MODELES_SUGGERES.map((m) => {
                  const dejaCree = liste.some((x) => x.nom === m.nom)
                  return (
                    <Btn key={m.nom} $variant="ghost" $size="sm" onClick={() => nouveau(m)}>
                      {m.nom}{dejaCree ? ' ↺' : ''}
                    </Btn>
                  )
                })}
              </Cluster>
            </Panel>
          )}

        </Col>

        {selected && (
          <Col>
            <Stagger>
              <Panel $accent>
                <PanelHead>
                  <PanelTitle>{selected === 'new' ? 'Nouveau modèle' : form.nom || 'Modèle'}</PanelTitle>
                  <Btn $variant="quiet" $size="sm" onClick={() => setSelected(null)}>✕ Fermer</Btn>
                </PanelHead>

                <Field>
                  <Label>Nom du modèle</Label>
                  <Input value={form.nom} onChange={(e) => setForm((p) => ({ ...p, nom: e.target.value }))}
                    placeholder="ex : Offre courte, Relance après 10 jours…" />
                </Field>

                <Field>
                  <Label>Disponibilité</Label>
                  <Bascule $on={form.actif} type="button"
                    onClick={() => setForm((p) => ({ ...p, actif: !p.actif }))}>
                    {form.actif
                      ? '✅ Proposé dans la liste des devis'
                      : '🚫 Masqué — cliquez pour le reproposer'}
                  </Bascule>
                </Field>

                <Field>
                  <Label>Objet du mail</Label>
                  <Input value={form.sujet} onChange={(e) => setForm((p) => ({ ...p, sujet: e.target.value }))}
                    placeholder="Votre offre achatpaille.com — {{produits}}" />
                </Field>

                <Label>Variables — cliquez pour insérer</Label>
                <Jetons>
                  {VARIABLES.map((v) => (
                    <Jeton key={v.cle} type="button" title={v.aide} onClick={() => inserer(v.cle)}>
                      {`{{${v.cle}}}`}
                    </Jeton>
                  ))}
                </Jetons>

                <Field>
                  <Label>Corps du mail</Label>
                  <Textarea id="corps-modele" value={form.corps}
                    onChange={(e) => setForm((p) => ({ ...p, corps: e.target.value }))}
                    style={{ minHeight: 260, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: '0.78rem' }}
                    placeholder={'Bonjour {{nom}},\n\n…'} />
                </Field>

                <Details>
                  <summary>Phrases de délai propres à ce modèle (facultatif)</summary>
                  <Muted style={{ fontSize: '0.75rem', margin: '10px 0 12px', display: 'block' }}>
                    {'{{livraison}}'} choisit seule entre ces quatre situations, selon la
                    marchandise et la date demandée. Laissez une case vide pour garder la
                    formulation par défaut. Variables acceptées ici :{' '}
                    {VARIABLES_LIVRAISON.map((v) => `{{${v.cle}}}`).join(', ')}.
                  </Muted>
                  {CAS_LIVRAISON.map((cas) => (
                    <Field key={cas.cle}>
                      <Label>{cas.label}</Label>
                      <Textarea
                        value={form.phrases?.[cas.cle] || ''}
                        onChange={(e) => setForm((p) => ({
                          ...p, phrases: { ...p.phrases, [cas.cle]: e.target.value },
                        }))}
                        style={{ minHeight: 70, fontSize: '0.78rem' }}
                        placeholder="Par défaut" />
                    </Field>
                  ))}
                </Details>

                <Field>
                  <Label>Aperçu sur un devis d'exemple</Label>
                  <Cluster $gap="7px" style={{ marginBottom: 9, flexWrap: 'wrap' }}>
                    {SCENARIOS.map((sc) => (
                      <Chip key={sc.cle} $on={scenario === sc.cle} onClick={() => setScenario(sc.cle)}>
                        {sc.label}
                      </Chip>
                    ))}
                  </Cluster>
                  <Apercu>{apercu() || 'Le corps est vide.'}</Apercu>
                </Field>

                <Muted style={{ fontSize: '0.74rem', marginBottom: 14, display: 'block' }}>
                  Ce client est fictif : dans un vrai devis, les variables prennent les valeurs
                  de la demande. C'est pourquoi {'{{livraison}}'} n'écrit pas la même chose selon
                  la marchandise et la date — d'où les deux scénarios ci-dessus.
                  Une variable inconnue, elle, reste affichée telle quelle : c'est le signe d'une
                  faute de frappe, corrigez-la avant d'envoyer.
                </Muted>

                <Cluster $end $gap="9px">
                  {selected !== 'new' && <Btn $variant="danger" onClick={supprimer}>Supprimer</Btn>}
                  <Btn $variant="gold" onClick={enregistrer} disabled={saving}>
                    {saving ? 'Enregistrement…' : 'Enregistrer'}
                  </Btn>
                </Cluster>
              </Panel>
            </Stagger>
          </Col>
        )}
      </Split>
    </>
  )
}
